import { useEffect, useMemo, useRef, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { SandboxFrameHandle } from "../renderers/sandbox-frame"
import type { ArtifactEntry } from "../stores/artifact-store"

import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { CodeBlock } from "@/features/chat/components/code-block"
import { useCreateDocument } from "@/hooks/use-documents"

import { getArtifactRegistryEntry } from "../registry"
import { ArtifactPreview } from "./artifact-preview"
import { ArtifactShareDialog } from "./artifact-share-dialog"
import { parseChartSpec } from "../renderers/chart-spec"
import { useArtifactStore } from "../stores/artifact-store"

export function ArtifactPanel({ identifier }: { identifier: string }) {
  const { t } = useTranslation()
  const entry = useArtifactStore((s) => s.artifacts[identifier] as ArtifactEntry | undefined)
  const markArtifactShared = useArtifactStore((s) => s.markArtifactShared)
  const [activeVersion, setActiveVersion] = useState<number | null>(null) // null = latest
  // Default to preview; only a streaming artifact starts on the code tab (the
  // effect below flips it). A complete/saved artifact opened from the library
  // never streams in this mount, so it must not default to raw source.
  const [tab, setTab] = useState<"preview" | "code">(() =>
    entry?.status === "streaming" ? "code" : "preview"
  )
  const [runtimeError, setRuntimeError] = useState<string | null>(null)
  const [shareOpen, setShareOpen] = useState(false)
  const [shareDocId, setShareDocId] = useState<string | null>(null)
  const createDocument = useCreateDocument()
  const userPickedTabRef = useRef(false)
  const wasStreamingRef = useRef(false)
  const printRef = useRef<SandboxFrameHandle>(null)
  const previewRef = useRef<HTMLDivElement>(null)

  const isStreaming = entry?.status === "streaming"

  // Reset per-artifact state when switching to a different artifact
  useEffect(() => {
    setActiveVersion(null)
    userPickedTabRef.current = false
  }, [identifier])

  // While streaming: show code live. When complete: switch to preview once —
  // respect any manual tab choice the user has made after that.
  // Use a ref to fire the auto-switch only on the streaming→complete transition,
  // not on every streaming chunk (entry is a new object each ingest).
  useEffect(() => {
    if (!entry) return
    if (isStreaming) {
      wasStreamingRef.current = true
      setTab("code")
    } else if (wasStreamingRef.current) {
      wasStreamingRef.current = false
      if (!userPickedTabRef.current) setTab("preview")
    } else if (!userPickedTabRef.current) {
      // Complete artifact that never streamed in this mount (saved library doc
      // or an already-finished session artifact): show the rendered preview,
      // not raw source. Respects a manual tab choice via userPickedTabRef.
      setTab("preview")
    }
  }, [entry, isStreaming])

  const versions = entry?.versions ?? []
  const versionIndex = activeVersion ?? versions.length - 1
  const content = versions[versionIndex]?.content ?? ""

  // Detect chart parse errors without calling any setter during render (C1).
  // If the spec is invalid, force the code tab regardless of user/auto choice.
  // Placed before the entry null-guard so hooks are called unconditionally.
  const chartParseError = useMemo(() => {
    if (entry?.type !== "chart") return null
    const r = parseChartSpec(content)
    return r.ok ? null : r.error
  }, [entry?.type, content])

  if (!entry) return null

  const registryEntry = getArtifactRegistryEntry(entry.type)

  const openShare = async () => {
    // Already document-backed (opened from the library, or saved/shared earlier
    // this session) → reuse that document rather than creating a duplicate.
    if (entry.imageSource?.kind === "document") {
      setShareDocId(entry.imageSource.documentId)
      setShareOpen(true)
      return
    }
    // Live artifact → save as a document first (copies images), then share it.
    const created = await createDocument.mutateAsync({
      title: entry.title,
      content,
      artifactType: entry.type,
      sourceSessionId:
        entry.imageSource?.kind === "session" ? entry.imageSource.sessionId : undefined,
      artifactMeta: entry.sessionFilenames?.length
        ? { sessionFilenames: entry.sessionFilenames }
        : undefined,
    })
    // Promote the store entry to a `document` source so a subsequent Share click
    // reuses this document instead of orphaning the participants just invited.
    // Pass the artifact's own session (not activeSessionId) — see markArtifactShared.
    markArtifactShared(entry.identifier, created.id, entry.sessionId)
    setShareDocId(created.id)
    setShareOpen(true)
  }

  const effectiveTab = chartParseError ? "code" : tab

  // Version button label: show "v1 / 3" when viewing an older version so the
  // user knows more versions exist; "v2" when on the latest.
  const isLatestVersion = versionIndex === versions.length - 1
  const versionLabel = isLatestVersion
    ? `v${String(versionIndex + 1)}`
    : `v${String(versionIndex + 1)} / ${String(versions.length)}`

  return (
    <div className="flex h-full flex-col">
      <Tabs
        className="flex min-h-0 flex-1 flex-col"
        value={effectiveTab}
        onValueChange={(v) => {
          userPickedTabRef.current = true
          setTab(v as "preview" | "code")
        }}
      >
        <div className="flex items-center gap-2 border-b px-3 py-2">
          <TabsList>
            {/* Disable Preview when the chart spec is invalid — the tab is forced
                to Code anyway, so a clickable Preview would be a dead control. */}
            <TabsTrigger disabled={chartParseError !== null} value="preview">
              {t("artifacts.tab_preview")}
            </TabsTrigger>
            <TabsTrigger value="code">{t("artifacts.tab_code")}</TabsTrigger>
          </TabsList>
          <span className="flex-1" />
          <Button
            disabled={isStreaming || createDocument.isPending}
            size="sm"
            variant="ghost"
            onClick={() => void openShare()}
          >
            {t("artifacts.share", { defaultValue: "Share" })}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline">
                {versionLabel}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {versions.map((v, i) => (
                <DropdownMenuItem
                  key={`${v.messageId}:${v.timestamp}:${i}`}
                  onClick={() => setActiveVersion(i)}
                >
                  {`v${String(i + 1)}`}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          {registryEntry.exporters.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline">
                  {t("artifacts.export")}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {registryEntry.exporters.map((exporter) => (
                  <DropdownMenuItem
                    key={exporter.id}
                    onClick={() => {
                      void exporter
                        .run({
                          entry,
                          content,
                          previewElement: previewRef.current,
                          requestPrint: printRef.current
                            ? () => printRef.current?.requestPrint()
                            : null,
                        })
                        .catch(() => toast.error(t("artifacts.export_failed")))
                    }}
                  >
                    {t(exporter.labelKey)}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {runtimeError ? (
          <div className="bg-destructive/10 text-destructive flex items-center gap-2 px-3 py-2 text-xs">
            <span className="min-w-0 flex-1 truncate">
              {t("artifacts.runtime_error", { message: runtimeError })}
            </span>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                navigator.clipboard
                  .writeText(runtimeError)
                  .then(() => toast.success(t("artifacts.error_copied")))
                  .catch(() => {
                    toast.error(t("artifacts.clipboard_failed"))
                  })
              }}
            >
              {t("artifacts.copy_error")}
            </Button>
          </div>
        ) : null}

        <TabsContent className="min-h-0 flex-1 overflow-auto" value="preview">
          <div ref={previewRef} className="h-full">
            <ArtifactPreview
              content={content}
              entry={entry}
              printRef={printRef}
              onRuntimeError={setRuntimeError}
            />
          </div>
        </TabsContent>
        <TabsContent className="min-h-0 flex-1 overflow-auto" value="code">
          <CodeBlock className={`language-${registryEntry.codeLanguage}`} isStreaming={isStreaming}>
            {content}
          </CodeBlock>
        </TabsContent>
      </Tabs>
      {shareOpen && shareDocId ? (
        <ArtifactShareDialog documentId={shareDocId} open={shareOpen} onOpenChange={setShareOpen} />
      ) : null}
    </div>
  )
}
